import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Container,
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Card,
  CardContent,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  AccountCircle,
  Email,
  Phone,
  CalendarToday,
} from "@mui/icons-material";

const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentUser, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    about: "",
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate("/sign-in");
    }
  }, [currentUser, navigate]);

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const response = await fetch("/api/users/profile", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUserData(data);
          setFormData({
            firstName: data.first_name || data.firstName || "",
            lastName: data.last_name || data.lastName || "",
            email: data.email || "",
            phone: data.phone || "",
            about: data.about || "",
          });
        } else {
          setError("Failed to load profile data");
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setError("");
      setLoading(true);

      await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        about: formData.about,
      });

      setSuccess("Profile updated successfully!");
      setIsEditing(false);

      // Refresh profile data
      const response = await fetch("/api/users/profile", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      }
    } catch (err) {
      setError(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original user data
    setFormData({
      firstName: userData.first_name || userData.firstName || "",
      lastName: userData.last_name || userData.lastName || "",
      email: userData.email || "",
      phone: userData.phone || "",
      about: userData.about || "",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (!currentUser) {
    return null;
  }

  if (loading && !userData) {
    return (
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        py: 8,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 4,
            color: "text.primary",
          }}
        >
          My Profile
        </Typography>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success" onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </Snackbar>
        )}

        {/* Profile Card */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "white",
          }}
        >
          <Grid container spacing={4}>
            {/* Avatar Section */}
            <Grid item xs={12} textAlign="center">
              <Avatar
                src={userData?.profile_picture || currentUser.profilePicture}
                sx={{
                  width: 120,
                  height: 120,
                  margin: "0 auto",
                  fontSize: "3rem",
                  bgcolor: "primary.main",
                }}
              >
                {userData?.first_name?.charAt(0) ||
                  currentUser.firstName?.charAt(0) ||
                  "U"}
              </Avatar>
              {!isEditing && (
                <Button
                  startIcon={<Edit />}
                  onClick={() => setIsEditing(true)}
                  sx={{ mt: 2 }}
                  variant="outlined"
                >
                  Edit Profile
                </Button>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Profile Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AccountCircle sx={{ mr: 2, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  First Name
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />
              ) : (
                <Typography variant="body1" sx={{ ml: 6, fontWeight: 500 }}>
                  {userData?.first_name || userData?.firstName || "N/A"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AccountCircle sx={{ mr: 2, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Last Name
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />
              ) : (
                <Typography variant="body1" sx={{ ml: 6, fontWeight: 500 }}>
                  {userData?.last_name || userData?.lastName || "N/A"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Email sx={{ mr: 2, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />
              ) : (
                <Typography variant="body1" sx={{ ml: 6, fontWeight: 500 }}>
                  {userData?.email || "N/A"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Phone sx={{ mr: 2, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />
              ) : (
                <Typography variant="body1" sx={{ ml: 6, fontWeight: 500 }}>
                  {userData?.phone || "N/A"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  About
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  name="about"
                  value={formData.about}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  placeholder="Write a brief bio about yourself..."
                />
              ) : (
                <Typography variant="body1" sx={{ ml: 0, fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                  {userData?.about || "No bio available"}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarToday sx={{ mr: 2, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Member Since
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: 6, fontWeight: 500 }}>
                {formatDate(userData?.createdAt)}
              </Typography>
            </Grid>

            {/* Edit Buttons */}
            {isEditing && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={loading}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Account Statistics */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Account Statistics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Articles Read
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    -
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Bookmarks
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    -
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Comments
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    -
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Profile;

